import type {
  AnswerMap,
  ComputedIntakeData,
  ComputedCondition,
  StageCondition,
  StageConditionGroup,
  TemplateVariable,
} from '../types/workflow'

export function getTextVariable(
  variables: TemplateVariable[] | undefined,
  title: string,
  fallback = ''
): string {
  const variable = variables?.find((v) => v.title === title)
  const value = variable?.value
  return typeof value === 'string' ? value : fallback
}

export function getArrayVariable<T = unknown>(
  variables: TemplateVariable[] | undefined,
  title: string,
  fallback: T[] = []
): T[] {
  const variable = variables?.find((v) => v.title === title)
  return Array.isArray(variable?.value) ? (variable.value as T[]) : fallback
}

export function getNumberVariable(
  variables: TemplateVariable[] | undefined,
  title: string,
  fallback = 0
): number {
  const variable = variables?.find((v) => v.title === title)
  const value = variable?.value
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? fallback : parsed
  }
  return fallback
}

export function getVariable<T = unknown>(
  variables: TemplateVariable[] | undefined,
  title: string
): T | undefined {
  const variable = variables?.find((v) => v.title === title)
  return variable?.value as T | undefined
}

function evaluateSingleCondition(condition: StageCondition, answers: AnswerMap): boolean {
  const { field, operator, value } = condition
  const answerValue = answers[field]
  switch (operator) {
    case 'equals':
      return answerValue === value
    case 'not_equals':
      return answerValue !== value
    case 'in':
      return Array.isArray(value) && value.includes(answerValue as never)
    case 'not_in':
      return Array.isArray(value) && !value.includes(answerValue as never)
    case 'contains':
      return Array.isArray(answerValue) && answerValue.includes(value as never)
    case 'not_contains':
      return Array.isArray(answerValue) && !answerValue.includes(value as never)
    case 'exists':
      return answerValue !== undefined && answerValue !== null && answerValue !== ''
    case 'not_exists':
      return answerValue === undefined || answerValue === null || answerValue === ''
    default:
      return true
  }
}

export function evaluateConditions(
  conditions: (StageCondition | StageConditionGroup)[] | undefined,
  answers: AnswerMap
): boolean {
  if (!conditions || conditions.length === 0) return true
  return conditions.every((condition) => {
    if ('or' in condition && condition.or) {
      return condition.or.some((subCondition) => evaluateSingleCondition(subCondition, answers))
    }
    if ('and' in condition && condition.and) {
      return condition.and.every((subCondition) => evaluateSingleCondition(subCondition, answers))
    }
    return evaluateSingleCondition(condition as StageCondition, answers)
  })
}

export function evaluateComputedConditions(
  computedConditions: ComputedCondition[] | undefined,
  computedData: ComputedIntakeData
): boolean {
  if (!computedConditions || computedConditions.length === 0) return true
  return computedConditions.every((condition) => {
    const computedValue = computedData[condition.computed]
    if (computedValue === undefined) return false
    const numValue = typeof computedValue === 'number' ? computedValue : parseFloat(String(computedValue))
    if (Number.isNaN(numValue)) return false
    switch (condition.operator) {
      case '>=':
        return numValue >= condition.value
      case '>':
        return numValue > condition.value
      case '<=':
        return numValue <= condition.value
      case '<':
        return numValue < condition.value
      case '==':
        return numValue === condition.value
      case '!=':
        return numValue !== condition.value
      default:
        return true
    }
  })
}

export function validateDate(dateString: string | undefined): boolean {
  if (!dateString || dateString.length !== 10) return false
  const [month, day, year] = dateString.split('/').map(Number)
  if (month < 1 || month > 12) return false
  if (day < 1 || day > 31) return false
  if (year < 1900 || year > new Date().getFullYear()) return false
  const date = new Date(year, month - 1, day)
  return date.getMonth() === month - 1 && date.getDate() === day && date.getFullYear() === year
}

export function formatDateInput(value: string): string {
  const numbers = value.replace(/\D/g, '')
  if (numbers.length <= 2) return numbers
  if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`
  return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`
}

export function calculateProgress(currentIndex: number, totalStages: number): number {
  if (totalStages <= 0) return 0
  return Math.round(((currentIndex + 1) / totalStages) * 100)
}
